// A level move re-cuts only scopes at or below the shallowest level it moves.
import { PATH_SEPARATOR, type StudioDraft } from "../studioTypes";

/** Depth of a scope key — the root scope is "" and has depth 0. */
export const scopeDepthOf = (key: string): number =>
  key === "" ? 0 : key.split(PATH_SEPARATOR).length;

const carryForward = <T>(
  original: Readonly<Record<string, T>>,
  remeasured: Readonly<Record<string, T>>,
  carried: (key: string) => boolean,
): Record<string, T> => {
  const out: Record<string, T> = {};
  for (const [key, value] of Object.entries(remeasured)) {
    if (!carried(key)) out[key] = value;
  }
  for (const [key, value] of Object.entries(original)) {
    if (carried(key)) out[key] = value;
  }
  return out;
};

/** Merge a re-measured draft onto the original, keeping entries above the move. */
export const preserveUntouchedScopes = (
  original: StudioDraft,
  remeasured: StudioDraft,
  fromIndex: number,
  toIndex: number,
): StudioDraft => {
  const shallowest = Math.min(fromIndex, toIndex);
  if (shallowest <= 0) return remeasured;
  const aboveMove = (key: string) => scopeDepthOf(key) < shallowest;
  // bandOverrides key is [...path, value] — one depth deeper than its level.
  const bandAboveMove = (key: string) => scopeDepthOf(key) <= shallowest;
  return {
    ...remeasured,
    scopedValueOrder: carryForward(
      original.scopedValueOrder,
      remeasured.scopedValueOrder,
      aboveMove,
    ),
    orientOverrides: carryForward(original.orientOverrides, remeasured.orientOverrides, aboveMove),
    bandOverrides: carryForward(original.bandOverrides, remeasured.bandOverrides, bandAboveMove),
  };
};
